import { FC, useEffect, useMemo } from 'react';

import { listGiftBrandSellerDeliveries } from '@shared/api/giftDelivery/giftDelivery.controller';
import { GiftDeliveryStatus } from '@shared/api/giftDelivery/giftDelivery.interface';
import { Loading } from '@shared/components/Loading';
import { useAsyncFn } from 'react-use';

import { formatDate, getItemName } from '../../giftSeller/utils/delivery.utils';

// 정산관리: 배송완료 건 기준 정산 (백엔드 GiftBrandSettlement 미확정 — 금액/확정은 placeholder)
export const GiftBrandSettlementPage: FC = function GiftBrandSettlementPage() {
  const [{ loading, value: delivered }, fetchDelivered] = useAsyncFn(
    async () =>
      listGiftBrandSellerDeliveries(
        { deliveryStatus: GiftDeliveryStatus.Delivered },
        { 'shipping.deliveredAt': -1 },
      ),
    [],
  );

  useEffect(() => {
    fetchDelivered();
  }, [fetchDelivered]);

  const rows = useMemo(() => delivered ?? [], [delivered]);
  const totalQuantity = useMemo(
    () => rows.reduce((sum, d) => sum + (d.quantity || 0), 0),
    [rows],
  );

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold">정산 관리</h2>
        <p className="mt-1 text-sm text-gray-500">
          배송완료된 건을 기준으로 정산 대상을 확인하세요.
        </p>
      </div>

      {/* 정산 금액 기능 준비중 안내 */}
      <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
        <p className="font-semibold">정산 금액 계산 기능은 준비 중입니다.</p>
        <p className="mt-1 text-amber-700">
          건별 정산 금액과 정산 확정 기능은 곧 제공될 예정이며, 지금은 배송이 완료된
          건수와 수량만 보여드립니다.
        </p>
      </div>

      {/* 요약 */}
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div className="rounded-lg bg-white p-4 text-center shadow">
          <p className="text-2xl font-bold text-green-700">{rows.length}</p>
          <p className="text-sm text-gray-500">배송완료 건수</p>
        </div>
        <div className="rounded-lg bg-white p-4 text-center shadow">
          <p className="text-2xl font-bold text-indigo-700">{totalQuantity}</p>
          <p className="text-sm text-gray-500">총 수량</p>
        </div>
      </div>

      {loading ? (
        <div className="center-box py-16">
          <Loading />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-gray-500 shadow">
          정산할 배송완료 건이 없습니다.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">코드</th>
                <th className="px-3 py-2 text-left">상품</th>
                <th className="px-3 py-2 text-left">수량</th>
                <th className="px-3 py-2 text-left">수령인</th>
                <th className="px-3 py-2 text-left">배송완료일</th>
                <th className="px-3 py-2 text-right">정산금액</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((d) => (
                <tr key={d._id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-3 py-3 font-mono text-xs text-gray-500">
                    {d.fullCode || d.code || '-'}
                  </td>
                  <td className="px-3 py-3">
                    <p className="max-w-[180px] truncate font-medium">
                      {getItemName(d)}
                    </p>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3">{d.quantity}개</td>
                  <td className="whitespace-nowrap px-3 py-3">
                    {d.recipient?.name || '-'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500">
                    {formatDate(d.shipping?.deliveredAt)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-xs text-gray-400">
                    산정 예정
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button
          disabled
          title="정산 기능 준비 후 사용할 수 있습니다."
          className="cursor-not-allowed rounded-lg bg-gray-300 px-5 py-2 text-sm font-medium text-white"
        >
          정산 확정 (준비중)
        </button>
      </div>
    </div>
  );
};
